import { useState } from 'react';
import { Palette, Check } from 'lucide-react';

const THEMES = [
  {
    id: 'default',
    name: 'Calm Focus',
    description: 'Earthy tones for steady progress',
    colors: {
      bg: 'bg-stone-100',
      surface: 'bg-stone-50',
      primary: 'bg-[#E07A5F]',
      primaryHover: 'hover:bg-[#D46B4E]',
      success: 'bg-[#81B29A]',
      accent: 'text-[#E07A5F]',
      textPrimary: 'text-stone-900',
      textSecondary: 'text-stone-500',
      border: 'border-stone-200',
      rawText: '#1c1917',
      rawTextSecondary: '#78716c',
    },
  },
  {
    id: 'dread',
    name: 'Dread Mode',
    description: 'Dark urgency to power through',
    colors: {
      bg: 'bg-red-950',
      surface: 'bg-red-900',
      primary: 'bg-red-600',
      primaryHover: 'hover:bg-red-700',
      success: 'bg-orange-500',
      accent: 'text-red-400',
      textPrimary: 'text-red-50',
      textSecondary: 'text-red-300',
      border: 'border-red-800',
      rawText: '#fef2f2',
      rawTextSecondary: '#fca5a5',
    },
  },
  {
    id: 'procrastination',
    name: 'Anti-Procrastination',
    description: 'Bright energy to get started',
    colors: {
      bg: 'bg-amber-100',
      surface: 'bg-orange-50',
      primary: 'bg-orange-500',
      primaryHover: 'hover:bg-orange-600',
      success: 'bg-green-500',
      accent: 'text-orange-600',
      textPrimary: 'text-orange-950',
      textSecondary: 'text-orange-700',
      border: 'border-orange-300',
      rawText: '#431407',
      rawTextSecondary: '#c2410c',
    },
  },
  {
    id: 'flow',
    name: 'Flow State',
    description: 'Deep blue for concentration',
    colors: {
      bg: 'bg-blue-900',
      surface: 'bg-blue-800',
      primary: 'bg-blue-500',
      primaryHover: 'hover:bg-blue-600',
      success: 'bg-cyan-400',
      accent: 'text-blue-300',
      textPrimary: 'text-blue-50',
      textSecondary: 'text-blue-300',
      border: 'border-blue-700',
      rawText: '#eff6ff',
      rawTextSecondary: '#93c5fd',
    },
  },
  {
    id: 'night',
    name: 'Night Owl',
    description: 'Dark mode for late sessions',
    colors: {
      bg: 'bg-slate-900',
      surface: 'bg-slate-800',
      primary: 'bg-indigo-500',
      primaryHover: 'hover:bg-indigo-600',
      success: 'bg-emerald-500',
      accent: 'text-indigo-400',
      textPrimary: 'text-slate-50',
      textSecondary: 'text-slate-400',
      border: 'border-slate-700',
      rawText: '#f8fafc',
      rawTextSecondary: '#94a3b8',
    },
  },
  {
    id: 'energized',
    name: 'Energized',
    description: 'Vibrant purple for motivation',
    colors: {
      bg: 'bg-purple-800',
      surface: 'bg-purple-950',
      primary: 'bg-purple-500',
      primaryHover: 'hover:bg-purple-600',
      success: 'bg-pink-400',
      accent: 'text-purple-300',
      textPrimary: 'text-purple-50',
      textSecondary: 'text-purple-300',
      border: 'border-purple-700',
      rawText: '#faf5ff',
      rawTextSecondary: '#d8b4fe',
    },
  },
  {
    id: 'sunrise',
    name: 'Sunrise',
    description: 'Warm orange tiles on deep amber',
    colors: {
      bg: 'bg-orange-950',
      surface: 'bg-orange-800',
      primary: 'bg-orange-400',
      primaryHover: 'hover:bg-orange-500',
      success: 'bg-yellow-400',
      accent: 'text-orange-300',
      textPrimary: 'text-orange-50',
      textSecondary: 'text-orange-300',
      border: 'border-orange-700',
      rawText: '#fff7ed',
      rawTextSecondary: '#fdba74',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    description: 'Deep tiles on warm copper glow',
    colors: {
      bg: 'bg-orange-800',
      surface: 'bg-orange-950',
      primary: 'bg-orange-400',
      primaryHover: 'hover:bg-orange-500',
      success: 'bg-yellow-400',
      accent: 'text-orange-300',
      textPrimary: 'text-orange-50',
      textSecondary: 'text-orange-300',
      border: 'border-orange-700',
      rawText: '#fff7ed',
      rawTextSecondary: '#fdba74',
    },
  },
];

function ThemeSelector({ currentTheme, onThemeChange }) {
  const [showSelector, setShowSelector] = useState(false);

  return (
    <div className="relative" data-testid="theme-selector">
      <button
        onClick={() => setShowSelector(!showSelector)}
        className="p-2 rounded-md hover:bg-stone-100 text-stone-500 hover:text-stone-900 transition-colors"
        title="Change theme"
        data-testid="theme-selector-btn"
      >
        <Palette className="w-5 h-5" />
      </button>

      {showSelector && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowSelector(false)}
          ></div>
          <div className="absolute right-0 top-12 z-50 w-80 bg-white border border-stone-200 rounded-xl shadow-lg p-4">
            <h3
              className="text-lg font-medium text-stone-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Choose Your Mood
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {THEMES.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => {
                    onThemeChange(theme.id);
                    setShowSelector(false);
                  }}
                  data-testid={`theme-option-${theme.id}`}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    currentTheme === theme.id
                      ? 'border-[#E07A5F] bg-[#E07A5F]/5'
                      : 'border-stone-200 hover:border-stone-300 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="font-medium text-stone-900"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          {theme.name}
                        </span>
                        {currentTheme === theme.id && (
                          <Check className="w-4 h-4 text-[#E07A5F]" />
                        )}
                      </div>
                      <p
                        className="text-sm text-stone-500"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        {theme.description}
                      </p>
                    </div>
                    <div className="flex gap-1 ml-3">
                      <div className={`w-6 h-6 rounded ${theme.colors.bg} border border-stone-300`}></div>
                      <div className={`w-6 h-6 rounded ${theme.colors.primary}`}></div>
                      <div className={`w-6 h-6 rounded ${theme.colors.success}`}></div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export { ThemeSelector, THEMES };